import { type ReactElement, type ReactNode, useId } from 'react';
import { MyDesignCard, useMyDesignsQuery } from '@/entities/my-design';
import { MyDesignNameEditor } from '@/features/my-design/rename';
import { formatCount } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { MyDesignActions } from './my-design-actions';
import { MyDesignEmpty } from './my-design-empty';
import { MyDesignGridSkeleton } from './my-design-grid-skeleton';
import { MyDesignTools } from './my-design-tools';

/**
 * 내 디자인(designs.list) — 최근 수정순 카드 격자. 커서 페이지라 아래 "더 보기"로 이어 읽는다.
 * 본인인증 전 계정은 서버(activeProcedure)가 거부한다 — 그때도 다시 시도와 사유를 보여 준다.
 */
export const MyDesignGrid = (): ReactElement => {
  const query = useMyDesignsQuery();
  const headingId = useId();
  const designs = query.data?.pages.flatMap((page) => page.items) ?? [];

  const content = ((): ReactNode => {
    if (query.isPending) return <MyDesignGridSkeleton />;

    if (query.isError && designs.length === 0) {
      return (
        <div
          role="alert"
          className="flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center"
        >
          <p className="text-sm text-destructive">내 디자인을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      );
    }

    if (designs.length === 0) return <MyDesignEmpty />;

    return (
      <div className="flex flex-col gap-12">
        <ul className="grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 xl:grid-cols-4">
          {designs.map((design) => (
            <li key={design.id}>
              <MyDesignCard
                design={design}
                title={<MyDesignNameEditor design={design} />}
                tools={<MyDesignTools design={design} />}
                actions={<MyDesignActions design={design} />}
              />
            </li>
          ))}
        </ul>
        {query.hasNextPage ? (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="lg"
              className="h-12 border-foreground/20 px-10"
              isLoading={query.isFetchingNextPage}
              onClick={() => {
                void query.fetchNextPage();
              }}
            >
              더 보기
            </Button>
          </div>
        ) : null}
      </div>
    );
  })();

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2
            id={headingId}
            className="text-2xl leading-tight font-bold tracking-[-0.02em] sm:text-[1.75rem]"
          >
            내 디자인
          </h2>
          <p className="text-sm text-muted-foreground">
            저장한 디자인을 수정·복제하거나 바로 견적을 요청하세요. 이름을 누르면 바로 고칠 수
            있어요.
          </p>
        </div>
        {designs.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground tabular-nums">
              {formatCount(designs.length)}
              {query.hasNextPage ? '+' : ''}
            </span>
            개
          </p>
        ) : null}
      </div>
      {content}
    </section>
  );
};
